import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { RiStore2Line, RiSparkling2Fill, RiArrowRightSLine } from "react-icons/ri";
import { colors } from "@styles/colors";
import { useAuthReady } from "@provider/LineLoginProvider";
import { useUtilsStore } from "@utils/store/utils/useUtilsTool";

/**
 * 歡迎頁（/）：三個入口 —— 連結我的瓦斯行（既有客戶）、我是新用戶、已有帳號登入。
 *
 * 注意：本頁為使用者主動選擇入口的設計；實際 LIFF 流程中 LineLoginProvider 會在
 * 登入完成後自動導頁（登入成功 → /home；未註冊 → /register/...）。若要讓使用者實際
 * 停在此頁選擇，需另外調整 LineLoginProvider 的自動導頁邏輯。
 */
const WelcomePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const authReady = useAuthReady();
  const isLoading = useUtilsStore((s) => s.isLoading);
  const [show, setShow] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setShow(true), 100);
    return () => clearTimeout(t);
  }, []);

  // LIFF 開啟時若 URL 帶有 ?draft_id=xxx，直接導向該草稿訂單頁（保留原行為）
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const draftId = params.get("draft_id");
    if (draftId?.trim() && location.pathname !== `/quickOrder/${draftId}`) {
      navigate(`/quickOrder/${draftId}`, { replace: true });
    }
  }, [location.search, location.pathname, navigate]);

  // 自動登入／導流尚未結束前不要露出 Welcome（否則 rich menu 偶發先閃藍底歡迎頁再進 /home，
  // 體感像「fallback 頁 load 完才到主頁」，並疊出多次 loading）。
  if (!authReady || isLoading) {
    return (
      <div
        style={{
          minHeight: "100dvh",
          width: "100vw",
          background: colors.primary,
        }}
        aria-hidden
      />
    );
  }

  return (
    <div
      style={{
        minHeight: "100dvh",
        width: "100vw",
        background: colors.primary,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        position: "relative",
        fontFamily: "var(--font-sans)",
      }}>
      <style>{`
        @keyframes nu-fadeUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}
        @keyframes nu-shimmerLine{0%{background-position:-200% 0}100%{background-position:200% 0}}
        @keyframes nu-floatTank{0%,100%{transform:translateY(0) rotate(-4deg)}50%{transform:translateY(-8px) rotate(-1deg)}}
        @keyframes nu-floatStar1{0%,100%{transform:translateY(0) scale(1);opacity:0.7}50%{transform:translateY(-10px) scale(1.1);opacity:1}}
        @keyframes nu-floatStar2{0%,100%{transform:translateY(0) scale(1);opacity:0.5}50%{transform:translateY(-14px) scale(1.15);opacity:0.8}}
      `}</style>

      {/* === Hero 區 === */}
      <div
        style={{
          background: colors.primary,
          padding: "52px 24px 90px",
          borderRadius: "0 0 32px 32px",
          position: "relative",
          overflow: "hidden",
          color: "#fff",
        }}>
        {/* 微弱網格紋路 */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            opacity: 0.5,
            backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.08) 1px, transparent 1px)",
            backgroundSize: "22px 22px",
            pointerEvents: "none",
          }}
        />
        {/* 背景裝飾圓 */}
        <div
          style={{
            position: "absolute",
            top: -80,
            right: -80,
            width: 240,
            height: 240,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(255,255,255,0.15), transparent 70%)",
            pointerEvents: "none",
          }}
        />

        {/* 浮動瓦斯桶 */}
        <div
          style={{
            position: "absolute",
            top: 70,
            right: 24,
            opacity: show ? 1 : 0,
            transition: "opacity 1s 0.3s",
            animation: "nu-floatTank 4s ease-in-out infinite",
          }}>
          <svg width="64" height="78" viewBox="0 0 56 68" fill="none">
            <defs>
              <linearGradient id="welcome-tank" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="rgba(255,255,255,0.95)" />
                <stop offset="100%" stopColor="rgba(255,255,255,0.55)" />
              </linearGradient>
            </defs>
            <rect x="20" y="3" width="16" height="6" rx="2" fill="rgba(255,255,255,0.75)" />
            <rect x="18" y="3" width="4" height="12" rx="1.5" fill="rgba(255,255,255,0.75)" />
            <rect x="34" y="3" width="4" height="12" rx="1.5" fill="rgba(255,255,255,0.6)" />
            <rect x="24" y="11" width="8" height="6" rx="1.5" fill="rgba(255,255,255,0.65)" />
            <path
              d="M10 60 L10 22 Q10 16 14 14 Q22 12 28 12 Q34 12 42 14 Q46 16 46 22 L46 60 Q46 64 38 64 L18 64 Q10 64 10 60 Z"
              fill="url(#welcome-tank)"
            />
          </svg>
        </div>

        {/* 裝飾星星 */}
        <div style={{ position: "absolute", top: 56, right: 100, animation: "nu-floatStar1 3.2s ease-in-out infinite" }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <path d="M12 2l2 5 5 2-5 2-2 5-2-5-5-2 5-2 2-5z" fill="rgba(255,255,255,0.85)" />
          </svg>
        </div>
        <div style={{ position: "absolute", top: 158, right: 110, animation: "nu-floatStar2 4s ease-in-out infinite" }}>
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
            <path d="M12 2l2 5 5 2-5 2-2 5-2-5-5-2 5-2 2-5z" fill="rgba(255,255,255,0.7)" />
          </svg>
        </div>

        <div style={{ position: "relative", zIndex: 1 }}>
          {/* Logo */}
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              background: "rgba(255,255,255,0.15)",
              border: "1px solid rgba(255,255,255,0.25)",
              padding: "5px 12px 5px 6px",
              borderRadius: 20,
              marginBottom: 28,
              opacity: show ? 1 : 0,
              transition: "opacity 0.6s",
            }}>
            <div style={{ width: 20, height: 20, borderRadius: 6, background: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
                <path d="M13 2L4 14h7l-1 8 9-12h-7l1-8z" fill={colors.primary} />
              </svg>
            </div>
            <span style={{ fontSize: 11, fontWeight: 700, color: "#fff", letterSpacing: 1.5 }}>LPG CLOUD</span>
          </div>

          {/* 主標 */}
          <h1
            style={{
              fontSize: 28,
              fontWeight: 900,
              lineHeight: 1.32,
              margin: 0,
              letterSpacing: -0.5,
              animation: show ? "nu-fadeUp 0.6s ease 0.15s both" : "none",
            }}>
            <span style={{ fontSize: 17, fontWeight: 700, color: "rgba(255,255,255,0.88)" }}>您好 👋</span>
            <br />
            <span style={{ whiteSpace: "nowrap" }}>歡迎使用瓦斯雲端服務</span>
          </h1>

          <p
            style={{
              marginTop: 16,
              fontSize: 14,
              color: "rgba(255,255,255,0.82)",
              lineHeight: 1.65,
              maxWidth: 280,
              animation: show ? "nu-fadeUp 0.6s ease 0.25s both" : "none",
            }}>
            選擇符合您的身份開始,30 秒完成
          </p>
        </div>
      </div>

      {/* === 選項區 === */}
      <div
        style={{
          flex: 1,
          padding: "0 16px 24px",
          position: "relative",
          zIndex: 2,
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-end",
          animation: show ? "nu-fadeUp 0.5s ease 0.35s both" : "none",
        }}>
        {/* 連結我的瓦斯行（既有客戶） */}
        <div
          onClick={() => navigate("/register/existing")}
          style={{
            background: "#fff",
            borderRadius: 16,
            padding: "16px 18px",
            marginBottom: 22,
            cursor: "pointer",
            border: "1.5px solid #BFDBFE",
            boxShadow: "0 8px 24px rgba(33,150,243,0.15)",
            display: "flex",
            alignItems: "center",
            gap: 14,
            position: "relative",
            overflow: "hidden",
          }}>
          {/* 流動光帶 */}
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 2,
              background: "linear-gradient(90deg, transparent, #60A5FA, #2196f3, #60A5FA, transparent)",
              backgroundSize: "200% 100%",
              animation: "nu-shimmerLine 3s linear infinite",
            }}
          />
          {/* 最快標記 */}
          <div
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              background: "linear-gradient(135deg, #F59E0B, #EA580C)",
              color: "#fff",
              fontSize: 10,
              fontWeight: 800,
              padding: "3px 10px",
              borderBottomLeftRadius: 10,
              letterSpacing: 0.5,
            }}>
            最快
          </div>
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              background: "linear-gradient(135deg, #42A5F5, #2196f3)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              boxShadow: "0 4px 10px rgba(33,150,243,0.3)",
            }}>
            <RiStore2Line size={22} color="#fff" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: colors.primaryText, marginBottom: 3 }}>連結我的瓦斯行</div>
            <div style={{ fontSize: 12, color: colors.sub, lineHeight: 1.5 }}>有客戶序號可直接連結,快速完成綁定</div>
          </div>
          <div style={{ width: 32, height: 32, borderRadius: "50%", background: colors.primaryBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <RiArrowRightSLine size={20} color={colors.primary} />
          </div>
        </div>

        {/* 我是新用戶 */}
        <div
          onClick={() => navigate("/register/new")}
          style={{
            background: "#fff",
            borderRadius: 16,
            padding: "16px 18px",
            marginBottom: 20,
            cursor: "pointer",
            border: "1px solid #F5F5F5",
            boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
            display: "flex",
            alignItems: "center",
            gap: 14,
          }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: colors.bgGreen, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <RiSparkling2Fill size={22} color={colors.green} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: colors.grayDark, marginBottom: 3 }}>我是新用戶</div>
            <div style={{ fontSize: 12, color: colors.sub, lineHeight: 1.5 }}>為您推薦附近合作瓦斯行,線上完成簽約</div>
          </div>
          <div style={{ width: 32, height: 32, borderRadius: "50%", background: colors.bgGreen, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <RiArrowRightSLine size={20} color={colors.green} />
          </div>
        </div>

        {/* 登入 */}
        <div style={{ textAlign: "center", margin: "6px 0 2px" }}>
          <span onClick={() => navigate("/login")} style={{ fontSize: 13, color: "rgba(255,255,255,0.82)", cursor: "pointer" }}>
            已有帳號?
            <span style={{ fontWeight: 700, color: "#fff", textDecoration: "underline", textUnderlineOffset: 3, marginLeft: 2 }}>登入</span>
          </span>
        </div>

        {/* 條款 */}
        <div style={{ margin: "16px 4px 0", fontSize: 11, color: "rgba(255,255,255,0.78)", lineHeight: 1.6, textAlign: "center" }}>
          繼續即代表您同意我們的
          <span style={{ color: "#FCD34D", fontWeight: 600, marginLeft: 4 }}>服務條款</span>
          <span style={{ margin: "0 4px" }}>·</span>
          <span style={{ color: "#FCD34D", fontWeight: 600 }}>隱私政策</span>
        </div>
      </div>
    </div>
  );
};

export default WelcomePage;
